/**
 * Lab icon mapping configuration
 *
 * Maps icon name strings (used in labs.json) to MUI icon components.
 * Browse all available MUI icons at: https://mui.com/material-ui/material-icons/
 * To add a new icon, import it and add it to LAB_ICON_MAP below.
 */

import { ComponentType } from "react";
import {
  // Analytics & Charts
  Analytics,
  Assessment,
  BarChart,
  BubbleChart,
  CandlestickChart,
  DataUsage,
  DonutLarge,
  DonutSmall,
  Equalizer,
  Insights,
  Leaderboard,
  MultilineChart,
  PieChart,
  QueryStats,
  ScatterPlot,
  ShowChart,
  StackedBarChart,
  StackedLineChart,
  TableChart,
  Timeline,
  TrendingDown,
  TrendingFlat,
  TrendingUp,

  // Business & Finance
  AccountBalance,
  AccountBalanceWallet,
  AttachMoney,
  Business,
  BusinessCenter,
  CreditCard,
  CurrencyExchange,
  MonetizationOn,
  Paid,
  Payment,
  Payments,
  PointOfSale,
  PriceChange,
  PriceCheck,
  Receipt,
  ReceiptLong,
  RequestQuote,
  Savings,
  Sell,
  ShoppingCart,
  Store,
  Storefront,
  Work,
  WorkHistory,

  // Communication
  AlternateEmail,
  Announcement,
  CallMade,
  CallReceived,
  Campaign,
  Chat,
  ChatBubble,
  Comment,
  ContactMail,
  ContactPhone,
  Email,
  Forum,
  LiveHelp,
  MailOutline,
  Message,
  Notifications,
  NotificationsActive,
  PhoneInTalk,
  RssFeed,
  Send,
  SpeakerNotes,
  Textsms,
  Voicemail,

  // Content & Editing
  Article,
  AutoStories,
  Bookmark,
  ContentCopy,
  ContentCut,
  ContentPaste,
  Create,
  Description,
  DragIndicator,
  Draw,
  Edit,
  EditNote,
  Feed,
  Flag,
  FontDownload,
  FormatQuote,
  Grading,
  LibraryBooks,
  MenuBook,
  NoteAdd,
  Newspaper,
  PostAdd,
  Summarize,
  TextFields,
  TextSnippet,
  Translate,

  // Data & Database
  Backup,
  BackupTable,
  CloudSync,
  DataArray,
  DataObject,
  Dataset,
  FilterAlt,
  GridOn,
  GridView,
  JoinFull,
  JoinInner,
  JoinLeft,
  JoinRight,
  Merge,
  Reorder,
  Rule,
  Schema,
  Sort,
  SortByAlpha,
  Source,
  Storage,
  SwapVert,
  TableRows,
  TableView,
  Tag,
  ViewColumn,
  ViewList,
  ViewModule,

  // Development & Code
  Api,
  BugReport,
  BuildCircle,
  Code,
  CodeOff,
  Construction,
  DataExploration,
  DeveloperBoard,
  DeveloperMode,
  Extension,
  GitHub,
  Hub,
  Memory,
  Terminal,
  Webhook,
  WidgetsOutlined,

  // Devices & Hardware
  CastConnected,
  Computer,
  DesktopMac,
  DesktopWindows,
  DeviceHub,
  Devices,
  Dns,
  Keyboard,
  LaptopMac,
  Mouse,
  PhoneAndroid,
  PhoneIphone,
  Print,
  Router,
  Scanner,
  SettingsEthernet,
  SmartDisplay,
  Smartphone,
  SpeakerGroup,
  Tablet,
  Tv,
  Watch,

  // Files & Folders
  AttachFile,
  CloudDownload,
  CloudUpload,
  CreateNewFolder,
  Download,
  DriveFolderUpload,
  FileDownload,
  FilePresent,
  FileUpload,
  Folder,
  FolderOpen,
  FolderShared,
  FolderSpecial,
  Inventory,
  SnippetFolder,
  Topic,
  Upload,
  UploadFile,

  // General & Common
  AccountCircle,
  AccountTree,
  AdminPanelSettings,
  Apps,
  AutoAwesome,
  Bolt,
  Build,
  CalendarMonth,
  CalendarToday,
  Category,
  CheckCircle,
  Circle,
  Compress,
  CopyAll,
  Dangerous,
  Dashboard,
  DashboardCustomize,
  Delete,
  Downloading,
  Error,
  Explore,
  Favorite,
  Fingerprint,
  FlashOn,
  Group,
  Groups,
  Handyman,
  HealthAndSafety,
  Help,
  HighQuality,
  History,
  Home,
  HourglassBottom,
  HourglassTop,
  ImportExport,
  Info,
  Label,
  Language,
  Launch,
  LightMode,
  DarkMode,
  Link,
  List,
  LocalOffer,
  ManageAccounts,
  ManageSearch,
  OpenInNew,
  Pending,
  Person,
  PersonAdd,
  Power,
  PowerSettingsNew,
  Preview,
  Psychology,
  Public,
  QuestionAnswer,
  Recycling,
  Refresh,
  Report,
  Restore,
  RocketLaunch,
  Schedule,
  Search,
  Settings,
  SettingsApplications,
  Share,
  Speed,
  Star,
  Support,
  SupportAgent,
  Sync,
  TaskAlt,
  ThumbUp,
  Token,
  TouchApp,
  TravelExplore,
  Tune,
  Update,
  Upgrade,
  VerifiedUser,
  Warning,
  Widgets,

  // Maps & Location
  EditLocation,
  Layers,
  Map,
  MyLocation,
  NearMe,
  Navigation,
  PinDrop,
  Place,
  Route,
  SatelliteAlt,
  Terrain,

  // Media & Audio/Video
  AudioFile,
  Audiotrack,
  Camera,
  CameraAlt,
  GraphicEq,
  Headphones,
  Image,
  LibraryMusic,
  Mic,
  Movie,
  MusicNote,
  OndemandVideo,
  Photo,
  PhotoCamera,
  PhotoLibrary,
  PlayArrow,
  PlayCircle,
  Podcasts,
  Radio,
  SlowMotionVideo,
  Subscriptions,
  VideoLibrary,
  Videocam,
  VolumeUp,

  // Nature & Weather
  Air,
  Brightness5,
  Cloud,
  FilterDrama,
  Forest,
  Grass,
  NightsStay,
  Park,
  Thermostat,
  WaterDrop,
  Waves,

  // People & Social
  Diversity1,
  Diversity3,
  EmojiEmotions,
  EmojiPeople,
  Engineering,
  Face,
  Hive,
  People,
  PersonSearch,
  RecordVoiceOver,
  School,
  SelfImprovement,

  // Science & Education
  Biotech,
  Calculate,
  ChangeHistory,
  Functions,
  Hexagon,
  HistoryEdu,
  ModelTraining,
  Pageview,
  Palette,
  Pattern,
  Science,
  SmartToy,
  SsidChart,
  Straighten,
  Troubleshoot,
  Workspaces,

  // Security & Privacy
  Block,
  EnhancedEncryption,
  GppGood,
  GppMaybe,
  HttpsOutlined,
  Key,
  Lock,
  LockOpen,
  NoEncryption,
  Password,
  Policy,
  PrivacyTip,
  Security,
  Shield,
  VpnKey,
  VpnLock,
  Visibility,
  VisibilityOff,

  // System & Infrastructure
  CloudCircle,
  CloudDone,
  CloudOff,
  CloudQueue,
  DisplaySettings,
  ElectricalServices,
  Inventory2,
  Lan,
  MiscellaneousServices,
  Monitor,
  MonitorHeart,
  NetworkCheck,
  SettingsInputComponent,
  SettingsSystemDaydream,
  SignalCellularAlt,
  SignalWifi4Bar,
  SyncAlt,
  SystemUpdate,
  Timer,
  TrackChanges,
  Traffic,
  WifiTethering,

  // Travel & Transport
  AirplanemodeActive,
  DirectionsBike,
  DirectionsCar,
  DirectionsRun,
  DirectionsWalk,
  Flight,
  LocalShipping,
  Sailing,
  Train,
} from "@mui/icons-material";

export const LAB_ICON_MAP: Record<string, ComponentType> = {
  // Analytics & Charts
  Analytics,
  Assessment,
  BarChart,
  BubbleChart,
  CandlestickChart,
  DataUsage,
  DonutLarge,
  DonutSmall,
  Equalizer,
  Insights,
  Leaderboard,
  MultilineChart,
  PieChart,
  QueryStats,
  ScatterPlot,
  ShowChart,
  StackedBarChart,
  StackedLineChart,
  TableChart,
  Timeline,
  TrendingDown,
  TrendingFlat,
  TrendingUp,

  // Business & Finance
  AccountBalance,
  AccountBalanceWallet,
  AttachMoney,
  Business,
  BusinessCenter,
  CreditCard,
  CurrencyExchange,
  MonetizationOn,
  Paid,
  Payment,
  Payments,
  PointOfSale,
  PriceChange,
  PriceCheck,
  Receipt,
  ReceiptLong,
  RequestQuote,
  Savings,
  Sell,
  ShoppingCart,
  Store,
  Storefront,
  Work,
  WorkHistory,

  // Communication
  AlternateEmail,
  Announcement,
  CallMade,
  CallReceived,
  Campaign,
  Chat,
  ChatBubble,
  Comment,
  ContactMail,
  ContactPhone,
  Email,
  Forum,
  LiveHelp,
  MailOutline,
  Message,
  Notifications,
  NotificationsActive,
  PhoneInTalk,
  RssFeed,
  Send,
  SpeakerNotes,
  Textsms,
  Voicemail,

  // Content & Editing
  Article,
  AutoStories,
  Bookmark,
  ContentCopy,
  ContentCut,
  ContentPaste,
  Create,
  Description,
  DragIndicator,
  Draw,
  Edit,
  EditNote,
  Feed,
  Flag,
  FontDownload,
  FormatQuote,
  Grading,
  LibraryBooks,
  MenuBook,
  NoteAdd,
  Newspaper,
  PostAdd,
  Summarize,
  TextFields,
  TextSnippet,
  Translate,

  // Data & Database
  Backup,
  BackupTable,
  CloudSync,
  DataArray,
  DataObject,
  Dataset,
  FilterAlt,
  GridOn,
  GridView,
  JoinFull,
  JoinInner,
  JoinLeft,
  JoinRight,
  Merge,
  Reorder,
  Rule,
  Schema,
  Sort,
  SortByAlpha,
  Source,
  Storage,
  SwapVert,
  TableRows,
  TableView,
  Tag,
  ViewColumn,
  ViewList,
  ViewModule,

  // Development & Code
  Api,
  BugReport,
  BuildCircle,
  Code,
  CodeOff,
  Construction,
  DataExploration,
  DeveloperBoard,
  DeveloperMode,
  Extension,
  GitHub,
  Hub,
  Memory,
  Terminal,
  Webhook,
  WidgetsOutlined,

  // Devices & Hardware
  CastConnected,
  Computer,
  DesktopMac,
  DesktopWindows,
  DeviceHub,
  Devices,
  Dns,
  Keyboard,
  LaptopMac,
  Mouse,
  PhoneAndroid,
  PhoneIphone,
  Print,
  Router,
  Scanner,
  SettingsEthernet,
  SmartDisplay,
  Smartphone,
  SpeakerGroup,
  Tablet,
  Tv,
  Watch,

  // Files & Folders
  AttachFile,
  CloudDownload,
  CloudUpload,
  CreateNewFolder,
  Download,
  DriveFolderUpload,
  FileDownload,
  FilePresent,
  FileUpload,
  Folder,
  FolderOpen,
  FolderShared,
  FolderSpecial,
  Inventory,
  SnippetFolder,
  Topic,
  Upload,
  UploadFile,

  // General & Common
  AccountCircle,
  AccountTree,
  AdminPanelSettings,
  Apps,
  AutoAwesome,
  Bolt,
  Build,
  CalendarMonth,
  CalendarToday,
  Category,
  CheckCircle,
  Circle,
  Compress,
  CopyAll,
  Dangerous,
  Dashboard,
  DashboardCustomize,
  Delete,
  Downloading,
  Error,
  Explore,
  Favorite,
  Fingerprint,
  FlashOn,
  Group,
  Groups,
  Handyman,
  HealthAndSafety,
  Help,
  HighQuality,
  History,
  Home,
  HourglassBottom,
  HourglassTop,
  ImportExport,
  Info,
  Label,
  Language,
  Launch,
  LightMode,
  DarkMode,
  Link,
  List,
  LocalOffer,
  ManageAccounts,
  ManageSearch,
  OpenInNew,
  Pending,
  Person,
  PersonAdd,
  Power,
  PowerSettingsNew,
  Preview,
  Psychology,
  Public,
  QuestionAnswer,
  Recycling,
  Refresh,
  Report,
  Restore,
  RocketLaunch,
  Schedule,
  Search,
  Settings,
  SettingsApplications,
  Share,
  Speed,
  Star,
  Support,
  SupportAgent,
  Sync,
  TaskAlt,
  ThumbUp,
  Token,
  TouchApp,
  TravelExplore,
  Tune,
  Update,
  Upgrade,
  VerifiedUser,
  Warning,
  Widgets,

  // Maps & Location
  EditLocation,
  Layers,
  Map,
  MyLocation,
  NearMe,
  Navigation,
  PinDrop,
  Place,
  Route,
  SatelliteAlt,
  Terrain,

  // Media & Audio/Video
  AudioFile,
  Audiotrack,
  Camera,
  CameraAlt,
  GraphicEq,
  Headphones,
  Image,
  LibraryMusic,
  Mic,
  Movie,
  MusicNote,
  OndemandVideo,
  Photo,
  PhotoCamera,
  PhotoLibrary,
  PlayArrow,
  PlayCircle,
  Podcasts,
  Radio,
  SlowMotionVideo,
  Subscriptions,
  VideoLibrary,
  Videocam,
  VolumeUp,

  // Nature & Weather
  Air,
  Brightness5,
  Cloud,
  FilterDrama,
  Forest,
  Grass,
  NightsStay,
  Park,
  Thermostat,
  WaterDrop,
  Waves,

  // People & Social
  Diversity1,
  Diversity3,
  EmojiEmotions,
  EmojiPeople,
  Engineering,
  Face,
  Hive,
  People,
  PersonSearch,
  RecordVoiceOver,
  School,
  SelfImprovement,

  // Science & Education
  Biotech,
  Calculate,
  ChangeHistory,
  Functions,
  Hexagon,
  HistoryEdu,
  ModelTraining,
  Pageview,
  Palette,
  Pattern,
  Science,
  SmartToy,
  SsidChart,
  Straighten,
  Troubleshoot,
  Workspaces,

  // Security & Privacy
  Block,
  EnhancedEncryption,
  GppGood,
  GppMaybe,
  HttpsOutlined,
  Key,
  Lock,
  LockOpen,
  NoEncryption,
  Password,
  Policy,
  PrivacyTip,
  Security,
  Shield,
  VpnKey,
  VpnLock,
  Visibility,
  VisibilityOff,

  // System & Infrastructure
  CloudCircle,
  CloudDone,
  CloudOff,
  CloudQueue,
  DisplaySettings,
  ElectricalServices,
  Inventory2,
  Lan,
  MiscellaneousServices,
  Monitor,
  MonitorHeart,
  NetworkCheck,
  SettingsInputComponent,
  SettingsSystemDaydream,
  SignalCellularAlt,
  SignalWifi4Bar,
  SyncAlt,
  SystemUpdate,
  Timer,
  TrackChanges,
  Traffic,
  WifiTethering,

  // Travel & Transport
  AirplanemodeActive,
  DirectionsBike,
  DirectionsCar,
  DirectionsRun,
  DirectionsWalk,
  Flight,
  LocalShipping,
  Sailing,
  Train,
};

/**
 * Get icon component by name, with fallback to Science icon
 */
export function getLabIcon(name: string): ComponentType {
  return LAB_ICON_MAP[name] || Science;
}
